export { clearAllChatModels, clearChatModel, useChatModel } from './lib';
export {
  createChatModel,
  type ChatModel,
  type ChatState,
  type ChatWindowApi,
  type Message,
} from './model';

import { useCallback, useEffect, useRef } from 'react';
import { createChatId } from '../../shared/messageHub';
import { ChatWindowModelProvider } from './lib/chatwindowcontext';
import { useChatModel } from './lib/useChatModel';
import { useChatScroll } from './lib/useChatScroll';
import type { ChatWindowApi } from './model';
import { ChatMessageList } from './ui/chatMessageList';
import { MessageInput } from './ui/messageInput';
import { ScrollToBottomBtn } from './ui/scrollToBottomBtn';

interface ChatWindowProps {
  className?: string;
  messageListVariant?: 'default' | 'ticket';
  ticketId?: string;
  autoScrollToBottomOnMount?: boolean;
  api: ChatWindowApi;
}

export const ChatWindow = (props: ChatWindowProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hasAutoScrolledRef = useRef(false);

  const chatId = props.ticketId
    ? createChatId({ type: 'ticket', ticketId: props.ticketId })
    : createChatId({ type: 'personal' });

  const {
    model,
    messages,
    isLoadingMore,
    hasMoreMessages,
    loadMessages,
    loadMoreMessages,
    setApi,
    error,
  } = useChatModel(chatId);

  const {
    scrollContainerRef,
    showScrollButton,
    hasNewMessages,
    newMessagesCount,
  } = useChatScroll({
    loadMore: () => loadMoreMessages(),
    enabled: true,
    messages,
    hasMoreMessages,
    isLoadingMore,
  });

  useEffect(() => {
    setApi(props.api);
  }, [setApi, props.api]);

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'auto') => {
      const container = scrollContainerRef.current;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
      messagesEndRef.current?.scrollIntoView({
        behavior: behavior,
        block: 'end',
      });
    },
    [scrollContainerRef]
  );

  useEffect(() => {
    loadMessages();

    const timeout = setTimeout(() => scrollToBottom('auto'), 200);
    return () => clearTimeout(timeout);
  }, [chatId, loadMessages, scrollToBottom]);

  useEffect(() => {
    if (!props.autoScrollToBottomOnMount) return;
    if (hasAutoScrolledRef.current) return;
    if (messages.length === 0) return;

    hasAutoScrolledRef.current = true;

    requestAnimationFrame(() => {
      scrollToBottom('auto');
      requestAnimationFrame(() => scrollToBottom('auto'));
    });
  }, [messages.length, props.autoScrollToBottomOnMount, scrollToBottom]);

  useEffect(() => {
    hasAutoScrolledRef.current = false;
  }, [chatId]);

  return (
    <ChatWindowModelProvider value={model}>
      <div
        className={`flex flex-col rounded-lg bg-white ${props.className || ''}`}
      >
        <ChatMessageList
          variant={props.messageListVariant}
          scrollContainerRef={scrollContainerRef}
          messagesEndRef={messagesEndRef}
        />

        <div
          className={` bg-white relative  border-t-gray-200 border-t ${
            props.messageListVariant === 'ticket'
              ? '-mx-6 px-4 pt-6 mt-6'
              : ' p-3'
          }`}
        >
          {error && (
            <div className="mb-2 text-sm text-red-600" role="alert">
              {error}
            </div>
          )}
          {showScrollButton && (
            <div className="absolute -top-12 right-4 z-10">
              <ScrollToBottomBtn
                onClick={() => scrollToBottom('smooth')}
                hasNewMessages={hasNewMessages}
                newMessagesCount={newMessagesCount}
              />
            </div>
          )}

          <MessageInput />
        </div>
      </div>
    </ChatWindowModelProvider>
  );
};
